import type { PlanTier } from '@/types/billing';

const VALID_PLANS: PlanTier[] = ['free', 'team', 'growth', 'enterprise'];

/** Extract validated plan from search params. Returns null if invalid. */
export function getValidPlanIntent(params: URLSearchParams): PlanTier | null {
  const raw = params.get('plan');
  return raw && VALID_PLANS.includes(raw as PlanTier) ? (raw as PlanTier) : null;
}

/** Whether a redirect target is a safe app-local path (open-redirect guard). */
export function isSafeLocalPath(value: string | null): value is string {
  return (
    !!value &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\') &&
    !value.includes('://')
  );
}

/** Compute post-auth redirect. A safe `redirect` wins over `plan`; unsafe ones are ignored. */
export function getPostAuthRedirect(params: URLSearchParams): string {
  const redirect = params.get('redirect');
  if (isSafeLocalPath(redirect)) return redirect;
  const plan = getValidPlanIntent(params);
  if (plan && plan !== 'free') return `/usage-billing?plan=${plan}`;
  return '/dashboard';
}

/** Build cross-link URL preserving plan + redirect params. */
export function buildAuthSwitchUrl(
  target: 'login' | 'signup',
  currentParams: URLSearchParams,
): string {
  const preserved = new URLSearchParams();
  const plan = currentParams.get('plan');
  const redirect = currentParams.get('redirect');
  if (plan) preserved.set('plan', plan);
  if (redirect) preserved.set('redirect', redirect);
  const qs = preserved.toString();
  return `/${target}${qs ? `?${qs}` : ''}`;
}

/**
 * Build the `/welcome` profiling URL, preserving `redirect` + `plan` so the
 * post-profiling navigation lands on the originally intended destination
 * (e.g. an AWS Marketplace `?ref=` link) instead of defaulting to /dashboard.
 */
export function buildWelcomeUrl(currentParams: URLSearchParams): string {
  const preserved = new URLSearchParams();
  const plan = currentParams.get('plan');
  const redirect = currentParams.get('redirect');
  if (plan) preserved.set('plan', plan);
  if (redirect) preserved.set('redirect', redirect);
  const qs = preserved.toString();
  return `/welcome${qs ? `?${qs}` : ''}`;
}

/**
 * Query suffix (`?redirect=…` or '') that forwards the post-auth destination
 * through the OAuth round-trip, so OAuth buyers land back where they intended
 * (e.g. an AWS Marketplace `?ref=` link) instead of always on /welcome|/dashboard.
 */
export function oauthRedirectSuffix(params: URLSearchParams): string {
  const hasIntent = isSafeLocalPath(params.get('redirect')) || !!getValidPlanIntent(params);
  if (!hasIntent) return '';
  return `?redirect=${encodeURIComponent(getPostAuthRedirect(params))}`;
}
